import React, { useMemo } from 'react';
import { useAtomValue, useAtom } from 'jotai';
import { Drawer } from 'antd';
import { useSelector } from 'react-redux';
import { Retry } from '@components/common';
import ErrorBoundary from '@components/common/Retry/ErrorBoundary';
import {
  allDimensionsListSelector,
  dimensionsListSelector,
} from '@store/common/dimensionList/selectors';
import useAppDimension from '../hooks/useAppDimension';
import ExplorePanel from './ExplorePanel';
import { TableWrapper } from './styled';
import { TABLE_HEIGHT_MAPPING } from '../header/Resize';
import { heightAtom, unitAtom, exploreViewAtom, isTableCompareEnabledAtom } from '../atoms';
import Table from '@components/common/Table';
import useGrid from '../hooks/useGrid';
import { VisualSwitchProps } from './VisualSwitch';
import DimensionMap, { type DimensionMapRef } from './DimensionMap';
import apptracker from '@utils/sensor/apptracker';
import { TOGGLE_TO_MAP_VIEW } from '@src/utils/sensor/track';
import { useECOServerErrorMessage } from '@hooks/eco/useECOQueryError';
import { errorDataAtom } from '@src/components/metrics-panel-hz/atoms/index';
import SampledViewPanel from './SampledViewPanel/SampledViewPanel';
import DimensionWidgets from './DimensionWidgets/DimensionWidgets';

export interface AppDimensionTableProps {
  dimension: string;
  metrics?: string[];
  orderBy?: string;
  viewId?: string;
  disableDrilldown?: boolean;
  updateGrid: React.Dispatch<number>;
}

function AppDimensionTable({
  dimension,
  metrics: forcedMetrics,
  orderBy,
  viewId,
  updateGrid,
  disableDrilldown,
}: AppDimensionTableProps) {
  const [visualType, setVisualType] = React.useState<VisualSwitchProps['value']>('table');
  const unit = useAtomValue(unitAtom);
  const height = useAtomValue(heightAtom);
  const errorData = useAtomValue(errorDataAtom);
  const dimensionList: any = useSelector(allDimensionsListSelector);
  const [exploreView, setExploreView] = useAtom(exploreViewAtom);
  const dimensionDetails = dimensionList.find((d) => d.groupBy === dimension);
  const isTableCompareEnabled = useAtomValue(isTableCompareEnabledAtom);

  const {
    data,
    totals,
    columns,
    isFetching,
    isError,
    isEmpty,
    refetch,
    sortBy,
    onColumnSort,
    onSearch,
    searchText,
    pctShareMaxValues,
    metrics,
    error: metricQueryError,
    groupByEmptyStateMessage,
  } = useAppDimension({
    dimension,
    metrics: forcedMetrics,
    orderBy,
    viewId,
    unit,
    disableDrilldown,
    doNotSort: dimensionDetails?.isHighCardinality,
  });
  const mapRef = React.useRef<DimensionMapRef>(null);
  useGrid({ data, isError, isFetching, updateGrid, fixedHeight: visualType === 'map' });

  const handleVisualChange = (data: VisualSwitchProps['value']) => {
    setVisualType(data);
    if (data === 'map') {
      apptracker(TOGGLE_TO_MAP_VIEW, {
        dimension,
        unit,
        metrics: metrics.join(','),
      });
    }
  };

  React.useEffect(() => {
    mapRef.current?.resetMapCenter();
  }, [searchText]);

  const errorMsg = useECOServerErrorMessage(metricQueryError);

  // remove this check from here as well
  // when dimensionsListSelector has removed the filtering based on prefixes
  const originalDimensionName = useMemo(() => {
    if (dimension.startsWith('f_')) {
      return dimension.split('f_')[1];
    }
    if (dimension.startsWith('e_')) {
      return dimension.split('e_')[1];
    }
    if (dimension.startsWith('nwr_')) {
      return dimension.split('nwr_')[1];
    }
    return dimension;
  }, [dimension]);
  return (
    <>
      <DimensionWidgets
        dimension={dimension}
        onSearch={onSearch}
        searchText={searchText}
        visualType={visualType}
        handleVisualChange={handleVisualChange}
      />
      <TableWrapper style={{ height: TABLE_HEIGHT_MAPPING[height] }}>
        <Retry
          fetchData={refetch}
          isLoading={isFetching}
          showError={isError}
          isEmpty={isEmpty}
          progress={true}
          // groupByEmptyStateMessage: DPI network URL dimensions (and other HC dims) when IF sent
          // GROUP_BY_EMPTY_AFTER_SUBQUERY_ERRORS — see useAppDimension.
          errorMessage={errorMsg || groupByEmptyStateMessage}
          queryDimensionError={errorData[dimension]}
        >
          {visualType === 'table' ? (
            <Table
              fixed={true}
              data={data}
              frozenData={[totals]}
              columns={columns}
              headerHeight={35}
              rowHeight={35}
              rowKey={dimension}
              sortBy={sortBy}
              headerProps={{ pctShareMaxValues, showTrend: isTableCompareEnabled }}
              onColumnSort={onColumnSort}
            />
          ) : (
            <DimensionMap
              ref={mapRef}
              defaultMetric={sortBy.key}
              dimension={dimension}
              unit={unit}
              data={data}
            />
          )}
        </Retry>
      </TableWrapper>
      {exploreView.show && exploreView.dimension === dimension && (
        <Drawer
          open={exploreView.show}
          placement="bottom"
          className="explore-drawer"
          onClose={() => setExploreView({ show: false, dimension: null })}
          destroyOnHidden={true}
          title={dimensionDetails.name}
          height="90%"
        >
          <ErrorBoundary>
            {exploreView.isHighCardinality ? (
              <SampledViewPanel dimension={originalDimensionName} />
            ) : (
              <ExplorePanel
                dimension={dimension}
                data={data}
                columns={columns}
                dimensionDetails={dimensionDetails}
              />
            )}
          </ErrorBoundary>
        </Drawer>
      )}
    </>
  );
}

export default React.memo(AppDimensionTable);
